//
// CONSTANTS
// Description: List of commonly used Constant Variables or Settings
// Note       : Do not mix with Enums
//              Capitalize and Underscore your variables
//

import moment from 'moment'

// System
export const UNAUTHORIZED_TYPE = {
  ACCOUNT_ACTIVATION: 1,
  PASSWORD_EXPIRED: 2,
}
export const UNAUTHORIZED_REASON = {
  ACCOUNT_ACTIVATION: 'Unauthorized - Account must be activated with new password',
  PASSWORD_EXPIRED: 'Unauthorized - Password expired',
}

// DateTimeFormat
export const DATE_PICKER_FORMAT = 'dd/MM/yyyy'
export const DATE_PICKER_FORMAT_MOMENT = 'DD/MM/YYYY'
export const DATE_PICKER_DATE_TIME_FORMAT_MOMENT = 'DD/MM/YYYY hh:mm a'
export const DATE_PICKER_SHORT_TIME_ONLY = 'hh:mm a'
export const DATE_ONLY = 'DD MMMM yyyy'
export const DATE_SHORT_MONTH_ONLY = 'DD MMM yyyy'
export const DATE_TIME_ONLY = 'DD MMMM yyyy hh:mm:ss A'
export const DATE_SHORT_TIME_ONLY = 'DD MMMM yyyy hh:mm A'
export const DATE_SHORT_MONTH_SHORT_TIME_ONLY = 'DD MMM yyyy hh:mm A'
export const ORDINAL_DATE_ONLY = 'Do MMMM yyyy'
export const ORDINAL_DATE_TIME_ONLY = 'Do MMMM yyyy hh:mm:ss A'
export const TIME_ONLY = 'hh:mm:ss A'
export const SHORT_TIME_ONLY = 'hh:mm A'
export const SHORT_TIME_24_ONLY = 'HH:mm'
export const MONTH_YEAR_ONLY = 'MMM yyyy'
export const DATE_ONLY_BACKEND = 'YYYY-MM-DD'
export const YEAR_ONLY = 'yyyy'

// Event Emitter
export const EVENT_EMITTER = {
  // Format: MODULE > COMPONENT > ACTION
  // PROFILE: {
  //   PROFILE_PHOTO: {
  //     UPLOADED: 'Profile.ProfilePhoto.Uploaded',
  //   },
  // },
  NOTIFICATION: {
    UNREAD_NOTIFICATION: {
      UNREAD_NOTIFICATION_COUNT: 'Notification.UnreadNotification.UnreadNotificationCount',
    },
  },
}

// File Format
export const FILE_FORMAT = {
  PROFILE_PHOTO: {
    MIME_TYPE: 'image/jpeg, image/png',
    EXTENSION: '.jpg, .jpeg, .png',
    SIZE_IN_KB: 500,
  },
}

// Session Storage
export const SESSION_STORAGE = {
  // Format: MODULE > COMPONENT > VARIABLE_NAME
  TABLE: {
    PAGINATION: {
      SELECTED_OPTION_PER_PAGE: 'Table.Pagination.SelectedOptionPerPage',
    },
  },
}

// Default Number of Maximum Files for Dropzone
export const MAX_DROPZONE_FILES = 5

// Default Yup Validation Schema
export const VALIDATION = {
  PASSWORD: {
    LOGIN: {
      MAX: 50,
    },
    FORM: {
      MIN: 6,
      MAX: 50,
      MIN_LOWERCASE: 1,
      MIN_UPPERCASE: 1,
      MIN_NUMBERS: 1,
      MIN_SYMBOLS: 1,
    },
  },
  URL: {
    FORM: {
      PATTERN: /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,4}\b([-a-zA-Z0-9@:%_\+.~#?&\/\/=]*)/g,
    },
  },
  PHONE: {
    FORM: {
      PATTERN: /^[\+]?[(]?[0-9]{2,3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
    },
  },
}

// Default Yup Validation Error Message
export const VALIDATION_ERROR = {
  // Based on Value Type
  STRING: {
    REQUIRED: () => {
      return 'This field is required'
    },
    REQUIREDx: (fieldName: string) => {
      return `${fieldName} is required`
    },
    MIN: (number: number, unit?: 'character' | 'digit' | string) => {
      return `Minimum ${number} ${unit ?? 'character'}${number > 1 ? 's' : ''}`
    },
    MAX: (number: number, unit?: 'character' | 'digit' | string) => {
      return `Maximum ${number} ${unit ?? 'character'}${number > 1 ? 's' : ''}`
    },
    EMAIL: () => {
      return 'This email is invalid'
    },
    URL: () => {
      return 'This link is invalid'
    },
    PHONE: () => {
      return 'This number is invalid'
    },
  },
  NUMBER: {
    REQUIRED: () => {
      return 'This field is required'
    },
    REQUIREDx: (fieldName: string) => {
      return `${fieldName} is required`
    },
    MIN: (number: number) => {
      return `This field must be at least ${number}`
    },
    MAX: (number: number) => {
      return `This field must be at most ${number}`
    },
    LESS_THAN: (number: number) => {
      return `This field must be less than ${number}`
    },
    MORE_THAN: (number: number) => {
      return `This field must be more than ${number}`
    },
    INTEGER: () => {
      return `This field must be a whole number`
    },
  },
  BOOLEAN: {
    REQUIRED: () => {
      return 'This field is required'
    },
    REQUIREDx: (fieldName: string) => {
      return `${fieldName} is required`
    },
  },
  DATE: {
    REQUIRED: () => {
      return 'This field is required'
    },
    REQUIREDx: (fieldName: string) => {
      return `${fieldName} is required`
    },
    MIN: (placeholder: Date | string) => {
      // placeholder = date or field name
      if (placeholder instanceof Date) {
        return `This field must later than ${moment(placeholder).format(DATE_PICKER_FORMAT)}`
      } else {
        return `This field must later than ${placeholder}`
      }
    },
    MAX: (placeholder: Date | string) => {
      // placeholder = date or field name
      if (placeholder instanceof Date) {
        return `This field must earlier than ${moment(placeholder).format(DATE_PICKER_FORMAT)}`
      } else {
        return `This field must earlier than ${placeholder}`
      }
    },
  },
  ARRAY: {
    REQUIRED: () => {
      return 'This field is required'
    },
    REQUIREDx: (fieldName: string) => {
      return `${fieldName} is required`
    },
    MIN: (number: number, fieldName: string) => {
      return `Minimum ${number} ${fieldName} needed`
    },
    MAX: (number: number, fieldName: string) => {
      return `Maximum ${number} ${fieldName} only`
    },
  },
  PASSWORD: {
    REQUIRED: () => {
      return 'This field is required'
    },
    REQUIREDx: (fieldName: string) => {
      return `${fieldName} is required`
    },
    MIN: (number: number) => {
      return `Password must be at least ${number} character${number > 1 ? 's' : ''}`
    },
    MAX: (number: number, unit?: 'character' | 'digit' | string) => {
      return `Password must not more than ${number} character${number > 1 ? 's' : ''}`
    },
    MIN_LOWERCASE: (number: number) => {
      return `Password must contain at least ${number} lower case letter${number > 1 ? 's' : ''}`
    },
    MIN_UPPERCASE: (number: number) => {
      return `Password must contain at least ${number} upper case letter${number > 1 ? 's' : ''}`
    },
    MIN_NUMBERS: (number: number) => {
      return `Password must contain at least ${number} number${number > 1 ? 's' : ''}`
    },
    MIN_SYMBOLS: (number: number) => {
      return `Password must contain at least ${number} special character${number > 1 ? 's' : ''}`
    },
  },

  // Based on Input Type
  SELECT: {
    NOT_AVAILABLE: (placeholder?: string | string[]) => {
      if (Array.isArray(placeholder)) {
        const items = placeholder.map((item) => `<li> ${item}`).join('<br/></li>')
        return `The following options are no longer available, please select new options.<br/><ul>${items}</ul>`
      }
      return `${placeholder ?? 'The selected option'} is no longer available. Please select a new option.`
    },
  },
}

// Default Chart Display Color Set
export const CHART_COLOR = [
  '#40B4E5', // Sky Blue
  '#95D600', // Lime Green
  '#FFB602', // Orange
  '#ED1C24', // Red
  '#26547C', // Navy Blue
  '#EF476F', // Pink
  '#FFD166', // Yellow
  '#06D6A0', // Teal
  '#D1BCE3', // Lavender
  '#FF1493', // Deep Pink
  '#0BD8F4', // Cyan
  '#CACC90', // Olive
  '#4169E1', // Royal Blue Violet
  '#8B008B', // Dark Magenta
  '#B8860B', // Dark Goldenrod
  '#4BE2D5', // Turquoise
  '#FF69B4', // Hot Pink
  '#F1D8A7', // Beige
  '#8B4513', // Brown
  '#FF6B35', // Coral
  '#9B59B6', // Purple
  '#228B22', // Forest Green
  '#E67E22', // Pumpkin
  '#708090', // Slate Gray Blue
  '#FFD700', // Bright Gold
  '#16A085', // Sea Green
  '#C0392B', // Maroon
  '#7F8C8D', // Gray
  '#32CD32', // Lime
  '#DC143C', // Crimson
  '#00FA9A', // Medium Spring Green
  '#FF6347', // Tomato
  '#556B2F', // Dark Olive Green
  '#4682B4', // Steel Blue
  '#DAA520', // Goldenrod
  '#2E8B57', // Sea Green Dark
  '#FF4500', // Orange Red
  '#BA55D3', // Medium Orchid
  '#CD853F', // Peru
  '#20B2AA', // Light Sea Green
]

// Modal Messages
export const MODAL_MESSAGE = {
  CLOSE_UNSAVED_CHANGES: 'The system will not save any changes. Are you sure you want to close?',
}

// Toastify Settings
export const TOAST_SETTING = {
  SUCCESS: {
    autoClose: 5000,
  },
  ERROR: {
    autoClose: 10000,
  },
}

// Select Settings
export const SELECT_SETTING = {
  CLEAR_VALUE: true,
   DROPDOWN_MENU_PORTAL_Z_INDEX: 1046, // Higher than default offcanvas but below default modal
}

// Chart Golden Ratio
export const CHART_SETTING = {
  FUNNEL: {
    MIN_RATIO: 50,
  },
  TREEMAP: {
    MIN_PERCENTAGE: 0.01,
  },
}

// Miscellaneous
export const MALAYSIA_COUNTRY_ID = 104
export const UNSPECIFIED_STATE_ID = 17
export const UNSPECIFIED_STATE_NAME = 'Unspecified State'
export const UNSPECIFIED_DISTRICT_ID = 9999
export const UNSPECIFIED_DISTRICT_NAME = 'Unspecified District'
export const DISASTER_VICTIMS_BENEFICIARY_TYPE_ID = 17
export const MAX_INT = 2_000_000_000
export const MAX_BIG_INT = 900_000_000_000_000
